/**
 * @design-contract CUR-113 + CUR-206
 *
 * Owner of the latest-turn runway measurement. Encapsulates:
 *   - `activeProgressSlotMinHeight` (height reservation for the PMV slot
 *     so it doesn't collapse when an expanded disclosure recedes mid-
 *     stream, while still allowing it to collapse to 0 when the slot's
 *     children disappear — CUR-113's "what is this man" ghost-rectangle
 *     bug guard).
 *   - `activeTurnRunwayMinHeight` (overall reserved height between the
 *     pinned user message and the composer / message-stack trailing
 *     gap; serves as scroll runway so the response can grow without
 *     yanking the reader).
 *   - `activeResponseRunwayHeight` (padding applied to the assistant
 *     area to keep the response from being clipped while the rest of
 *     the turn hasn't expanded yet).
 *   - `activeTurnBottomSpacerHeight` (the visible spacer below the
 *     active turn — reconciles two competing constraints: composer
 *     occlusion overlap when the response has consumed its runway, and
 *     no extra padding for short responses).
 *
 * INSTRUCTIONS FOR HUMAN OR AI AGENTS EDITING THIS FILE:
 *   - DO NOT add `activeResponseRunwayHeight` or
 *     `activeTurnBottomSpacerHeight` to the measurement effect's
 *     dependency array, or set them via state instead of refs. Doing so
 *     re-enters an infinite measurement loop. We've observed runway
 *     values climbing past 200,000 px before React kills the render
 *     with "Maximum update depth exceeded".
 *   - DO NOT remove the per-CHILD ResizeObserver registration on the
 *     active progress slot. A slot-only observer never fires when a
 *     child shrinks while the slot is clamped at a larger min-height,
 *     so the recede logic stops running and disclosures get stuck at
 *     their expanded height.
 *   - DO NOT measure the slot via `getBoundingClientRect().height` or
 *     `scrollHeight` and feed the result back into setState. The slot's
 *     own min-height is part of its bounding rect; that path makes the
 *     min-height strictly non-decreasing. Sum CHILDREN rects instead.
 *   - DO NOT add a standalone `+ messageStackTrailingGap` to the bottom
 *     spacer. The trailing gap is already baked into
 *     `measuredTurnRunwayHeight` for short responses; adding it twice
 *     produces a turn that is exactly 20 px taller than the equivalent
 *     non-active path, which shows up as "extra padding on long
 *     responses that refreshing makes disappear".
 *   - DO NOT make `composerOcclusion` additive unconditionally. Only
 *     apply it when the response runway has been consumed
 *     (`nextResponseRunwayHeight === 0`); otherwise short turns end up
 *     with ~100 px of weird padding below the response.
 *
 * Each of these rules has shipped at least one regression — see the
 * CUR-113 history and the ChatDisplay.test.tsx test names that cite
 * specific values like 614px, 200px, 220px, 20px.
 */

import { useLayoutEffect, useRef, useState } from 'react'
import {
  activeTurnRunwayMinimumHeightPx,
  activeTurnRunwayViewportRatio,
  messageStackTrailingGapPx,
  pinnedUserMessageTopGapPx,
  type ChatSurfaceFlags
} from './chatLayoutTokens'

/**
 * Branded readonly view of the runway state. Consumers receive this from
 * the hook and CANNOT substitute literals. Without the brand, a callsite
 * that "knows the runway should be 320" could pass `{ ... height: 320 }`
 * and silently bypass the measurement loop.
 */
export interface RunwayState {
  readonly __brand: 'RunwayState'
  readonly activeProgressSlotMinHeight: number | null
  readonly activeTurnRunwayMinHeight: number | null
  readonly activeResponseRunwayHeight: number | null
  readonly activeTurnBottomSpacerHeight: number | null
  readonly activeTurnRunwayReady: boolean
}

/** Pluggable debug-flag reader. Default reads `localStorage` like the
 *  original code; tests can override. */
function isRunwayDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false
  const flag =
    window.localStorage.getItem('chat_debug_runway') ??
    window.localStorage.getItem('chat_debug_markdown')
  return ['1', 'true', 'on', 'yes'].includes(flag?.toLowerCase() ?? '')
}

export interface UseTurnRunwayOptions extends ChatSurfaceFlags {
  /** True iff we should reserve runway for the latest turn. False ends
   *  the effect early and clears all state to null. */
  shouldReserveActiveTurnRunway: boolean
  /** The scroll container element (state-stored, NOT ref). */
  scrollContainer: HTMLDivElement | null
  /** The latest turn wrapper (the flex column containing user + assistant). */
  latestTurnRef: React.RefObject<HTMLDivElement | null>
  /** The user message bubble within the latest turn. */
  latestTurnUserMessageRef: React.RefObject<HTMLDivElement | null>
  /** The active PMV slot. */
  activeProgressSlotRef: React.RefObject<HTMLDivElement | null>
  /** The active assistant response slot. */
  activeResponseSlotRef: React.RefObject<HTMLDivElement | null>
  /** The composer panel (fixed at the viewport bottom). */
  composerPanelRef: React.RefObject<HTMLDivElement | null>
  /** Effect dep — re-measure when the latest turn changes identity. */
  latestUserMessageId: string | null
  /** Effect dep — re-measure when the active response reveals more text. */
  activeResponseRevealRevision: number
  /** Effect dep — re-measure when the message array shape changes. */
  processedMessagesIdentity: unknown
  /** Effect dep — flipped when the user sends a new prompt. */
  isLoading: boolean
  /** Effect dep — replaced active-runway match (post-stream refetch). */
  latestUserMatchesActiveRunway: boolean
  /**
   * Effect dep — re-measure when the composer panel resizes. The
   * ResizeObserver on `composerPanelRef` already catches this in steady
   * state, but threading the measured height through deps gives the
   * effect a guaranteed extra fire on the first commit where the
   * composer height becomes known. Symptom of dropping it: short turns
   * sized BEFORE the composer mounts retain a 220px spacer instead of
   * the correct ~20px composer-overlap value.
   */
  composerPanelHeight: number
}

/**
 * Returns `candidate` unless accepting it would re-create the value we last
 * DEPARTED from — i.e. unless we'd be flipping back to the value held one
 * change ago. A 2-cycle break for setters in feedback-prone hooks where the
 * underlying formula's output depends on layout measurements that the setter
 * itself influences.
 *
 * Pass `lastDeparted` from a ref kept by the consumer; pass the returned
 * `lastDeparted` back into the ref after each call. Honest growth/shrink
 * sequences (A → B → C with C ≠ A) pass through. A 2-cycle (A → B → A)
 * latches at B and stays there until the formula's output moves to a
 * genuinely new value.
 *
 * Exported for direct unit testing.
 */
export function pickNonCyclingValue<T>(
  current: T,
  candidate: T,
  lastDeparted: T | null
): { value: T; lastDeparted: T | null } {
  if (Object.is(current, candidate)) {
    return { value: current, lastDeparted }
  }
  if (Object.is(candidate, lastDeparted)) {
    return { value: current, lastDeparted }
  }
  return { value: candidate, lastDeparted: current as T | null }
}

export function useTurnRunway(options: UseTurnRunwayOptions): RunwayState {
  const {
    shouldReserveActiveTurnRunway,
    scrollContainer,
    latestTurnRef,
    latestTurnUserMessageRef,
    activeProgressSlotRef,
    activeResponseSlotRef,
    composerPanelRef,
    latestUserMessageId,
    activeResponseRevealRevision,
    processedMessagesIdentity,
    isLoading,
    latestUserMatchesActiveRunway,
    composerPanelHeight
  } = options

  const surfaceFlags: ChatSurfaceFlags = {
    isPlugin: options.isPlugin,
    useLandingColumnLayout: options.useLandingColumnLayout
  }
  const pinnedUserMessageTopGap = pinnedUserMessageTopGapPx(surfaceFlags)
  const messageStackTrailingGap = messageStackTrailingGapPx(surfaceFlags)
  const activeTurnRunwayMinimumHeight = activeTurnRunwayMinimumHeightPx(surfaceFlags)
  const activeTurnRunwayViewportRatioValue = activeTurnRunwayViewportRatio(surfaceFlags)

  const [activeProgressSlotMinHeight, setActiveProgressSlotMinHeight] = useState<number | null>(
    null
  )
  const [activeTurnRunwayMinHeight, setActiveTurnRunwayMinHeight] = useState<number | null>(null)
  const [activeResponseRunwayHeight, setActiveResponseRunwayHeight] = useState<number | null>(null)
  const [activeTurnBottomSpacerHeight, setActiveTurnBottomSpacerHeight] = useState<number | null>(
    null
  )

  // ───────────────────────────────────────────────────────────────────────
  // INVARIANT (FRAGILE): These two refs MUST be read inside the runway
  // measurement effect via `*Ref.current` and the corresponding state values
  // MUST stay out of that effect's dependency array. See file banner.
  // ───────────────────────────────────────────────────────────────────────
  const activeResponseRunwayHeightRef = useRef<number | null>(null)
  const activeTurnBottomSpacerHeightRef = useRef<number | null>(null)
  activeResponseRunwayHeightRef.current = activeResponseRunwayHeight
  activeTurnBottomSpacerHeightRef.current = activeTurnBottomSpacerHeight

  // Bottom-spacer 2-cycle break. The runway formula's bottom-spacer output
  // depends on a `composerOverlapNeeded` switch (line ~431) that, mid-stream,
  // can swap between 0 and `composerOcclusion` (~120 px) based on layout
  // measurements that the spacer itself influences via flex-layout coupling.
  // The result is a stable 2-attractor: spacer at A → formula outputs B;
  // spacer at B → formula outputs A. The effect re-fires on every
  // ResizeObserver tick from the streaming response slot's growth, so the
  // toggle runs sub-frame (~5 ms between flips) and the user sees frame-rate
  // visual chaos — what we've been calling the "spazz".
  //
  // The full fix would refactor the formula to remove the layout-feedback
  // dependency. As an immediate guard: reject any setter call that would
  // flip back to the value we held one tick ago. Honest growth/shrink
  // sequences (A → B → C with C ≠ A) pass through. See
  // `pickNonCyclingValue` for the pure semantics + tests.
  const lastDepartedBottomSpacerValueRef = useRef<number | null>(null)

  // Reset latched values when the latest turn identity changes — the
  // first frame for a new turn measures from scratch.
  useLayoutEffect(() => {
    setActiveProgressSlotMinHeight(null)
    setActiveTurnRunwayMinHeight(null)
    setActiveResponseRunwayHeight(null)
    setActiveTurnBottomSpacerHeight(null)
    lastDepartedBottomSpacerValueRef.current = null
  }, [latestUserMessageId])

  // ─────────────────────────────────────────────────────────────────────
  // Active PMV slot min-height. Children-sum measurement with per-child
  // ResizeObserver. See file banner for why both rules are non-negotiable.
  // ─────────────────────────────────────────────────────────────────────
  useLayoutEffect(() => {
    if (!shouldReserveActiveTurnRunway || !activeProgressSlotRef.current) {
      setActiveProgressSlotMinHeight(null)
      return
    }

    const updateActiveProgressSlotMinHeight = () => {
      const progressSlot = activeProgressSlotRef.current
      if (!progressSlot) return

      // No children: collapse the latched min-height to 0. Early-returning
      // here (instead of writing 0) leaves a tall ghost rectangle when
      // multiple PMVs disappear in a single frame.
      if (progressSlot.childElementCount === 0) {
        setActiveProgressSlotMinHeight(currentHeight => (currentHeight === 0 ? currentHeight : 0))
        return
      }

      // Children sum, not getBoundingClientRect or scrollHeight (those
      // include the slot's own min-height and make it monotonic).
      let contentHeight = 0
      for (const child of Array.from(progressSlot.children)) {
        if (!(child instanceof HTMLElement)) continue
        contentHeight += child.getBoundingClientRect().height
      }
      const nextMinHeight = Math.ceil(contentHeight || progressSlot.scrollHeight)
      if (nextMinHeight <= 0) return

      setActiveProgressSlotMinHeight(currentHeight =>
        currentHeight === nextMinHeight ? currentHeight : nextMinHeight
      )
    }

    updateActiveProgressSlotMinHeight()

    if (typeof ResizeObserver === 'undefined') return

    const resizeObserver = new ResizeObserver(updateActiveProgressSlotMinHeight)
    const progressSlot = activeProgressSlotRef.current
    const observedChildren = new Set<HTMLElement>()

    const observeProgressChild = (child: Element) => {
      if (child instanceof HTMLElement && !observedChildren.has(child)) {
        observedChildren.add(child)
        resizeObserver.observe(child)
      }
    }

    const unobserveProgressChild = (child: Element) => {
      if (child instanceof HTMLElement && observedChildren.has(child)) {
        observedChildren.delete(child)
        resizeObserver.unobserve(child)
      }
    }

    // MUST observe BOTH the slot and each child — see file banner.
    resizeObserver.observe(progressSlot)
    for (const child of Array.from(progressSlot.children)) {
      observeProgressChild(child)
    }

    const mutationObserver =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(mutations => {
            for (const mutation of mutations) {
              mutation.addedNodes.forEach(observeProgressChild)
              mutation.removedNodes.forEach(unobserveProgressChild)
            }
            updateActiveProgressSlotMinHeight()
          })
        : null
    mutationObserver?.observe(progressSlot, { childList: true })

    return () => {
      mutationObserver?.disconnect()
      resizeObserver.disconnect()
    }
  }, [processedMessagesIdentity, shouldReserveActiveTurnRunway, activeProgressSlotRef])

  // ─────────────────────────────────────────────────────────────────────
  // Runway-height + bottom-spacer measurement. The most fragile effect
  // in the chat surface. See the file banner for the four rules that
  // cannot be relaxed.
  // ─────────────────────────────────────────────────────────────────────
  useLayoutEffect(() => {
    const runwayDebugEnabled = isRunwayDebugEnabled()

    if (!shouldReserveActiveTurnRunway) {
      if (runwayDebugEnabled) {
        // eslint-disable-next-line no-console
        console.warn('[chat-runway-debug:skip]', {
          reason: 'shouldReserveActiveTurnRunway=false',
          latestUserMessageId,
          isLoading,
          latestUserMatchesActiveRunway
        })
      }
      setActiveTurnRunwayMinHeight(null)
      setActiveResponseRunwayHeight(null)
      setActiveTurnBottomSpacerHeight(null)
      return
    }

    const getFallbackTurnRunwayMinHeight = () => {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
      if (viewportHeight <= 0) return activeTurnRunwayMinimumHeight

      const viewportTarget = Math.round(viewportHeight * activeTurnRunwayViewportRatioValue)
      const viewportCeiling = Math.max(
        0,
        viewportHeight - pinnedUserMessageTopGap - messageStackTrailingGap
      )

      return Math.min(
        Math.max(activeTurnRunwayMinimumHeight, viewportTarget),
        Math.max(activeTurnRunwayMinimumHeight, viewportCeiling)
      )
    }

    const updateActiveTurnRunwayMinHeight = () => {
      const latestTurn = latestTurnRef.current

      if (!latestTurn || !scrollContainer) {
        const fallbackMinHeight = getFallbackTurnRunwayMinHeight()
        if (runwayDebugEnabled) {
          // eslint-disable-next-line no-console
          console.warn('[chat-runway-debug:fallback]', {
            reason: !latestTurn ? 'missing latestTurn' : 'missing scrollContainer',
            fallbackMinHeight,
            hasLatestTurn: !!latestTurn,
            hasScrollContainer: !!scrollContainer
          })
        }
        setActiveTurnRunwayMinHeight(currentHeight =>
          currentHeight === fallbackMinHeight ? currentHeight : fallbackMinHeight
        )
        setActiveResponseRunwayHeight(currentHeight =>
          currentHeight === fallbackMinHeight ? currentHeight : fallbackMinHeight
        )
        setActiveTurnBottomSpacerHeight(currentHeight => (currentHeight === 0 ? currentHeight : 0))
        return
      }

      const scrollContainerRect = scrollContainer.getBoundingClientRect()
      const latestTurnRect = latestTurn.getBoundingClientRect()
      const composerPanelRect = composerPanelRef.current?.getBoundingClientRect()
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
      const usableScrollBottom =
        composerPanelRect && composerPanelRect.top > 0
          ? Math.min(scrollContainerRect.bottom, composerPanelRect.top)
          : scrollContainerRect.bottom
      const measuredScrollBottom =
        viewportHeight > 0 ? Math.min(usableScrollBottom, viewportHeight) : usableScrollBottom
      const visibleScrollTop = Math.max(0, scrollContainerRect.top)
      const turnTopAfterPinnedScroll = visibleScrollTop + pinnedUserMessageTopGap

      if (measuredScrollBottom <= 0 || turnTopAfterPinnedScroll <= 0) {
        const fallbackMinHeight = getFallbackTurnRunwayMinHeight()
        if (runwayDebugEnabled) {
          // eslint-disable-next-line no-console
          console.warn('[chat-runway-debug:fallback]', {
            reason: 'invalid measured bounds',
            fallbackMinHeight,
            measuredScrollBottom,
            visibleScrollTop,
            turnTopAfterPinnedScroll
          })
        }
        setActiveTurnRunwayMinHeight(currentHeight =>
          currentHeight === fallbackMinHeight ? currentHeight : fallbackMinHeight
        )
        setActiveResponseRunwayHeight(currentHeight =>
          currentHeight === fallbackMinHeight ? currentHeight : fallbackMinHeight
        )
        setActiveTurnBottomSpacerHeight(currentHeight => (currentHeight === 0 ? currentHeight : 0))
        return
      }

      const measuredTurnRunwayHeight = Math.max(
        0,
        Math.ceil(measuredScrollBottom - turnTopAfterPinnedScroll - messageStackTrailingGap)
      )
      const currentResponseRunwayHeight = activeResponseRunwayHeightRef.current ?? 0
      const measuredTurnElements = [
        { name: 'user', element: latestTurnUserMessageRef.current },
        { name: 'progress', element: activeProgressSlotRef.current },
        { name: 'response', element: activeResponseSlotRef.current }
      ].map(({ name, element }) => {
        if (!element) return { name, element: null, rect: null, measuredHeight: 0 }

        const rect = element.getBoundingClientRect()
        const measuredHeight = Math.max(rect.height, element.scrollHeight)
        return { name, element, rect, measuredHeight }
      })
      const latestTurnContentBottom = measuredTurnElements.reduce(
        (bottom, { rect, measuredHeight }) => {
          if (!rect) return bottom
          return Math.max(bottom, rect.top + measuredHeight)
        },
        latestTurnRect.top
      )
      const measuredTurnContentHeight = Math.max(
        0,
        Math.ceil(latestTurnContentBottom - latestTurnRect.top)
      )
      const inferredTurnContentHeight = Math.max(
        0,
        latestTurnRect.height - currentResponseRunwayHeight
      )
      const turnContentHeight = Math.max(measuredTurnContentHeight, inferredTurnContentHeight)
      const nextMinHeight = measuredTurnRunwayHeight
      const nextResponseRunwayHeight = Math.max(0, nextMinHeight - turnContentHeight)
      const effectiveTurnHeight = turnContentHeight + nextResponseRunwayHeight

      // Composer occlusion overlap. Capped at viewport so a missing
      // `min-h-0` on the parent doesn't trigger the 200,000 px runaway.
      const cappedScrollBottom =
        viewportHeight > 0
          ? Math.min(scrollContainerRect.bottom, viewportHeight)
          : scrollContainerRect.bottom
      const composerOcclusion =
        composerPanelRect && composerPanelRect.top < cappedScrollBottom
          ? Math.max(0, Math.ceil(cappedScrollBottom - composerPanelRect.top))
          : 0
      const computedBottomSpacerHeight = Math.max(
        0,
        Math.ceil(measuredScrollBottom - turnTopAfterPinnedScroll - effectiveTurnHeight)
      )
      // ─────────────────────────────────────────────────────────────────
      // INVARIANT (FRAGILE): Bottom-spacer composition. Rules 1 + 2 in
      // the file banner. DO NOT REVERT either.
      // ─────────────────────────────────────────────────────────────────
      // The "response tail crosses the composer boundary" check covers
      // the streaming-reflow case where the latest turn temporarily sits
      // below its pinned target while text is still growing — the runway
      // is still positive but the response's tail is already under the
      // composer, so we DO need composer-overlap padding even though
      // `nextResponseRunwayHeight > 0`. Guarded by `sizes the active
      // turn runway to the composer boundary`.
      const responseTailCrossesComposerBoundary = latestTurnContentBottom > measuredScrollBottom
      const composerOverlapNeeded =
        nextResponseRunwayHeight > 0 && !responseTailCrossesComposerBoundary ? 0 : composerOcclusion
      const nextBottomSpacerHeight = Math.max(computedBottomSpacerHeight, composerOverlapNeeded)

      if (runwayDebugEnabled) {
        // eslint-disable-next-line no-console
        console.warn('[chat-runway-debug]', {
          activeResponseRunwayHeight: currentResponseRunwayHeight,
          nextResponseRunwayHeight,
          activeTurnBottomSpacerHeight: activeTurnBottomSpacerHeightRef.current,
          nextBottomSpacerHeight,
          computedBottomSpacerHeight,
          composerOcclusion,
          cappedScrollBottom,
          measuredTurnRunwayHeight,
          turnContentHeight,
          measuredTurnContentHeight,
          inferredTurnContentHeight,
          effectiveTurnHeight,
          latestTurn: {
            top: latestTurnRect.top,
            bottom: latestTurnRect.bottom,
            height: latestTurnRect.height
          },
          scrollContainer: {
            top: scrollContainerRect.top,
            visibleTop: visibleScrollTop,
            bottom: scrollContainerRect.bottom,
            height: scrollContainerRect.height
          },
          composerPanel: composerPanelRect
            ? {
                top: composerPanelRect.top,
                bottom: composerPanelRect.bottom,
                height: composerPanelRect.height
              }
            : null,
          measuredScrollBottom,
          turnTopAfterPinnedScroll,
          pinnedUserMessageTopGap,
          messageStackTrailingGap,
          elements: measuredTurnElements.map(({ name, rect, measuredHeight, element }) => ({
            name,
            measuredHeight,
            scrollHeight: element?.scrollHeight ?? 0,
            offsetHeight: element?.offsetHeight ?? 0,
            childCount: element?.childElementCount ?? 0,
            textLength: element?.textContent?.length ?? 0,
            rect: rect
              ? {
                  top: rect.top,
                  bottom: rect.bottom,
                  height: rect.height
                }
              : null
          }))
        })
      }

      setActiveTurnRunwayMinHeight(currentHeight =>
        currentHeight === nextMinHeight ? currentHeight : nextMinHeight
      )
      setActiveResponseRunwayHeight(currentHeight =>
        currentHeight === nextResponseRunwayHeight ? currentHeight : nextResponseRunwayHeight
      )
      setActiveTurnBottomSpacerHeight(currentHeight => {
        const { value, lastDeparted } = pickNonCyclingValue(
          currentHeight,
          nextBottomSpacerHeight,
          lastDepartedBottomSpacerValueRef.current
        )
        lastDepartedBottomSpacerValueRef.current = lastDeparted
        return value
      })
    }

    updateActiveTurnRunwayMinHeight()

    window.addEventListener('resize', updateActiveTurnRunwayMinHeight)

    let resizeObserver: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateActiveTurnRunwayMinHeight)
      if (scrollContainer) {
        resizeObserver.observe(scrollContainer)
      }
      if (latestTurnRef.current) {
        resizeObserver.observe(latestTurnRef.current)
      }
      if (activeProgressSlotRef.current) {
        resizeObserver.observe(activeProgressSlotRef.current)
      }
      if (activeResponseSlotRef.current) {
        resizeObserver.observe(activeResponseSlotRef.current)
      }
      if (composerPanelRef.current) {
        resizeObserver.observe(composerPanelRef.current)
      }
    }

    return () => {
      window.removeEventListener('resize', updateActiveTurnRunwayMinHeight)
      resizeObserver?.disconnect()
    }
    // ─────────────────────────────────────────────────────────────────────
    // INVARIANT (FRAGILE): DO NOT add `activeResponseRunwayHeight` or
    // `activeTurnBottomSpacerHeight` to this dep array (or `progress
    // slot min height` — that one is read transitively). See file banner
    // for the 200,000 px runaway loop this guards.
    // ─────────────────────────────────────────────────────────────────────
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeProgressSlotMinHeight,
    activeResponseRevealRevision,
    activeTurnRunwayMinimumHeight,
    activeTurnRunwayViewportRatioValue,
    composerPanelHeight,
    isLoading,
    latestUserMatchesActiveRunway,
    latestUserMessageId,
    messageStackTrailingGap,
    scrollContainer,
    pinnedUserMessageTopGap,
    processedMessagesIdentity,
    shouldReserveActiveTurnRunway
  ])

  const activeTurnRunwayReady =
    !shouldReserveActiveTurnRunway ||
    (activeTurnRunwayMinHeight !== null && activeTurnBottomSpacerHeight !== null)

  return {
    __brand: 'RunwayState',
    activeProgressSlotMinHeight,
    activeTurnRunwayMinHeight,
    activeResponseRunwayHeight,
    activeTurnBottomSpacerHeight,
    activeTurnRunwayReady
  }
}
